import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { I18nService } from './i18n.service';
import { BudgetHead, FinancialManagementOverview, ItemMaster, OFESEstablishment, PageResult, ProcurementTypeDashboardData, RequisitionDashboardData, StageDefinition, User } from './models';

@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly i18n = inject(I18nService);

  private headers(): HttpHeaders {
    const token = this.auth.token;
    const headers: Record<string, string> = { 'Accept-Language': this.i18n.language };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return new HttpHeaders(headers);
  }

  stages(): Observable<StageDefinition[]> {
    return this.http.get<StageDefinition[]>(`${environment.apiBaseUrl}/procurement/stages`, { headers: this.headers() });
  }

  budgetHeads(): Observable<BudgetHead[]> {
    return this.http.get<BudgetHead[]>(`${environment.apiBaseUrl}/master-data/budget-heads`, { headers: this.headers() });
  }

  establishments(): Observable<OFESEstablishment[]> {
    return this.http.get<OFESEstablishment[]>(`${environment.apiBaseUrl}/master-data/establishments`, { headers: this.headers() });
  }

  itemMaster(): Observable<ItemMaster[]> {
    return this.http.get<ItemMaster[]>(`${environment.apiBaseUrl}/master-data/items`, { headers: this.headers() });
  }

  itemTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${environment.apiBaseUrl}/master-data/item-types`, { headers: this.headers() });
  }

  financialManagement(financialYear: string): Observable<FinancialManagementOverview> {
    return this.http.get<FinancialManagementOverview>(`${environment.apiBaseUrl}/financial-management/overview`, {
      headers: this.headers(), params: new HttpParams().set('financial_year', financialYear)
    });
  }

  createFinancialBudgetHead(payload: Record<string, unknown>): Observable<Record<string, any>> {
    return this.http.post<Record<string, any>>(`${environment.apiBaseUrl}/financial-management/budget-heads`, payload, { headers: this.headers() });
  }

  updateFinancialBudgetHead(id: number, payload: Record<string, unknown>): Observable<Record<string, any>> {
    return this.http.put<Record<string, any>>(`${environment.apiBaseUrl}/financial-management/budget-heads/${id}`, payload, { headers: this.headers() });
  }

  deleteFinancialBudgetHead(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${environment.apiBaseUrl}/financial-management/budget-heads/${id}`, { headers: this.headers() });
  }

  createExpenditureHead(payload: Record<string, unknown>): Observable<Record<string, any>> {
    return this.http.post<Record<string, any>>(`${environment.apiBaseUrl}/financial-management/expenditure-heads`, payload, { headers: this.headers() });
  }

  updateExpenditureHead(id: number, payload: Record<string, unknown>): Observable<Record<string, any>> {
    return this.http.put<Record<string, any>>(`${environment.apiBaseUrl}/financial-management/expenditure-heads/${id}`, payload, { headers: this.headers() });
  }

  deleteExpenditureHead(id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${environment.apiBaseUrl}/financial-management/expenditure-heads/${id}`, { headers: this.headers() });
  }

  nextRequisitionNo(): Observable<{ requisition_no: string }> {
    return this.http.get<{ requisition_no: string }>(`${environment.apiBaseUrl}/procurement/requisitions/next-number`, { headers: this.headers() });
  }

  list(stage: string, page = 1, size = 10, search = ''): Observable<PageResult> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (search) params = params.set('search', search);
    return this.http.get<PageResult>(`${environment.apiBaseUrl}/procurement/${stage}`, { headers: this.headers(), params });
  }

  get(stage: string, id: number): Observable<Record<string, unknown>> {
    return this.http.get<Record<string, unknown>>(`${environment.apiBaseUrl}/procurement/${stage}/${id}`, { headers: this.headers() });
  }

  create(stage: string, payload: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.http.post<Record<string, unknown>>(`${environment.apiBaseUrl}/procurement/${stage}`, payload, { headers: this.headers() });
  }

  update(stage: string, id: number, payload: Record<string, unknown>): Observable<Record<string, unknown>> {
    return this.http.put<Record<string, unknown>>(`${environment.apiBaseUrl}/procurement/${stage}/${id}`, payload, { headers: this.headers() });
  }

  delete(stage: string, id: number): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${environment.apiBaseUrl}/procurement/${stage}/${id}`, { headers: this.headers() });
  }

  dashboard(startDate = '', endDate = ''): Observable<Record<string, any>> {
    let params = new HttpParams();
    if (startDate) params = params.set('start_date', startDate);
    if (endDate) params = params.set('end_date', endDate);
    return this.http.get<Record<string, any>>(`${environment.apiBaseUrl}/dashboard`, { headers: this.headers(), params });
  }

  requisitionDashboard(): Observable<RequisitionDashboardData> {
    return this.http.get<RequisitionDashboardData>(`${environment.apiBaseUrl}/dashboard/requisitions`, { headers: this.headers() });
  }

  procurementTypeDashboards(): Observable<ProcurementTypeDashboardData> {
    return this.http.get<ProcurementTypeDashboardData>(`${environment.apiBaseUrl}/dashboard/procurement-types`, { headers: this.headers() });
  }

  dashboardModule(
    moduleKey: string,
    filter: { period: string; month?: number; year?: number; startDate?: string; endDate?: string }
  ): Observable<Record<string, any>> {
    let params = new HttpParams().set('period', filter.period);
    if (filter.month) params = params.set('month', filter.month);
    if (filter.year) params = params.set('year', filter.year);
    if (filter.startDate) params = params.set('start_date', filter.startDate);
    if (filter.endDate) params = params.set('end_date', filter.endDate);
    return this.http.get<Record<string, any>>(`${environment.apiBaseUrl}/dashboard/module/${moduleKey}`, { headers: this.headers(), params });
  }

  report(path: string): Observable<any> {
    return this.http.get<any>(`${environment.apiBaseUrl}/reports/${path}`, { headers: this.headers() });
  }

  formalReport(
    reportKey: string,
    filter: { period: string; month?: number; year?: number; startDate?: string; endDate?: string }
  ): Observable<Record<string, any>> {
    return this.http.get<Record<string, any>>(`${environment.apiBaseUrl}/reports/formal/${reportKey}`, {
      headers: this.headers(),
      params: this.reportParams(filter)
    });
  }

  exportReport(
    reportKey: string,
    format: 'xlsx' | 'pdf',
    filter: { period: string; month?: number; year?: number; startDate?: string; endDate?: string }
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiBaseUrl}/reports/formal/${reportKey}/export`, {
      headers: this.headers(),
      params: this.reportParams(filter).set('format', format),
      observe: 'response',
      responseType: 'blob'
    });
  }

  customReportMetadata(): Observable<Record<string, any>> {
    return this.http.get<Record<string, any>>(`${environment.apiBaseUrl}/reports/custom/metadata`, { headers: this.headers() });
  }

  customReport(
    source: string,
    fields: string[],
    startDate = '',
    endDate = ''
  ): Observable<Record<string, any>> {
    return this.http.get<Record<string, any>>(`${environment.apiBaseUrl}/reports/custom`, {
      headers: this.headers(),
      params: this.customReportParams(source, fields, startDate, endDate)
    });
  }

  exportCustomReport(
    source: string,
    fields: string[],
    format: 'xlsx' | 'pdf',
    startDate = '',
    endDate = ''
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(`${environment.apiBaseUrl}/reports/custom/export`, {
      headers: this.headers(),
      params: this.customReportParams(source, fields, startDate, endDate).set('format', format),
      observe: 'response',
      responseType: 'blob'
    });
  }

  users(): Observable<User[]> {
    return this.http.get<User[]>(`${environment.apiBaseUrl}/users`, { headers: this.headers() });
  }

  createUser(payload: Record<string, unknown>): Observable<User> {
    return this.http.post<User>(`${environment.apiBaseUrl}/users`, payload, { headers: this.headers() });
  }

  updateUser(id: number, payload: Record<string, unknown>): Observable<User> {
    return this.http.put<User>(`${environment.apiBaseUrl}/users/${id}`, payload, { headers: this.headers() });
  }

  updateProfile(payload: { full_name: string; email: string | null }): Observable<User> {
    return this.http.put<User>(`${environment.apiBaseUrl}/auth/profile`, payload, { headers: this.headers() });
  }

  changePassword(payload: { current_password: string; new_password: string }): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${environment.apiBaseUrl}/auth/password`, payload, { headers: this.headers() });
  }

  private reportParams(filter: { period: string; month?: number; year?: number; startDate?: string; endDate?: string }): HttpParams {
    let params = new HttpParams().set('period', filter.period);
    if (filter.month) params = params.set('month', filter.month);
    if (filter.year) params = params.set('year', filter.year);
    if (filter.startDate) params = params.set('start_date', filter.startDate);
    if (filter.endDate) params = params.set('end_date', filter.endDate);
    return params;
  }

  private customReportParams(source: string, fields: string[], startDate: string, endDate: string): HttpParams {
    let params = new HttpParams().set('source', source);
    fields.forEach((field) => params = params.append('fields', field));
    if (startDate) params = params.set('start_date', startDate);
    if (endDate) params = params.set('end_date', endDate);
    return params;
  }
}
